export interface NudgePreferences {
  enabled: boolean;
  afternoonSlump: boolean;
  mealReminder: boolean;
}

export interface Nudge {
  id: string;
  type: "afternoon-slump" | "meal-reminder" | "streak";
  title: string;
  message: string;
  suggestion?: { food: string; reason: string };
  icon: string;
}

const AFTERNOON_SLUMP_SUGGESTIONS = [
  { food: "Handful of almonds", reason: "Protein and healthy fats for sustained energy" },
  { food: "Greek yogurt with berries", reason: "Low GI, high protein snack" },
  { food: "Apple slices with almond butter", reason: "Fiber and protein combo" },
  { food: "Cheese and whole grain crackers", reason: "Protein slows energy release" },
  { food: "Dark chocolate square", reason: "Low GI treat with antioxidants" },
  { food: "Hummus with veggie sticks", reason: "Fiber and plant protein" },
];

const MEAL_REMINDERS = [
  { title: "Lunch thinking time", message: "Planning a balanced lunch? Add protein first to lower the meal's GI impact." },
  { title: "Dinner prep reminder", message: "Tip: Start with a salad before your main course to reduce glucose spikes." },
  { title: "Breakfast boost", message: "Morning fuel: Pair your carbs with protein for steady energy all morning." },
];

export function getDefaultNudgePreferences(): NudgePreferences {
  return {
    enabled: true,
    afternoonSlump: true,
    mealReminder: true,
  };
}

export function loadNudgePreferences(): NudgePreferences {
  try {
    const saved = localStorage.getItem("nudge_preferences");
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore parse errors
  }
  return getDefaultNudgePreferences();
}

export function saveNudgePreferences(prefs: NudgePreferences): void {
  localStorage.setItem("nudge_preferences", JSON.stringify(prefs));
}

export function shouldShowNudge(prefs: NudgePreferences): Nudge | null {
  if (!prefs.enabled) return null;

  const now = new Date();
  const hour = now.getHours();
  const lastShown = localStorage.getItem("last_nudge_time");
  const lastShownDate = lastShown ? new Date(lastShown) : null;

  // Don't show more than one nudge per hour
  if (lastShownDate && (now.getTime() - lastShownDate.getTime()) < 60 * 60 * 1000) {
    return null;
  }

  // Afternoon slump: 2-4pm
  if (prefs.afternoonSlump && hour >= 14 && hour < 16) {
    const suggestion = AFTERNOON_SLUMP_SUGGESTIONS[Math.floor(Math.random() * AFTERNOON_SLUMP_SUGGESTIONS.length)];
    return {
      id: `afternoon-${now.toISOString()}`,
      type: "afternoon-slump",
      title: "Afternoon energy dip?",
      message: "Beat the 3pm slump with a smart snack choice.",
      suggestion,
      icon: "",
    };
  }

  // Meal reminders: around meal times
  if (prefs.mealReminder) {
    if (hour >= 11 && hour < 12) {
      return {
        id: `lunch-${now.toISOString()}`,
        type: "meal-reminder",
        ...MEAL_REMINDERS[0],
        icon: "",
      };
    }
    if (hour >= 17 && hour < 18) {
      return {
        id: `dinner-${now.toISOString()}`,
        type: "meal-reminder",
        ...MEAL_REMINDERS[1],
        icon: "",
      };
    }
  }

  return null;
}

export function markNudgeShown(): void {
  localStorage.setItem("last_nudge_time", new Date().toISOString());
}

export function dismissNudge(nudgeId: string): void {
  const dismissed = JSON.parse(localStorage.getItem("dismissed_nudges") || "[]");
  dismissed.push(nudgeId);
  localStorage.setItem("dismissed_nudges", JSON.stringify(dismissed.slice(-50))); // Keep last 50
  markNudgeShown();
}
